/**
 * Bureau Activity Monitoring Hooks
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { bureauActivityAPI } from '../api/bureau-activity.api';
import { teamAPI } from '../../equipe/api/team.api';
import {
  BureauUser,
  DailyActivity,
  UserActivitySummary,
  BureauStats,
  RealtimeActivity,
  UserActivityDetails,
  DateRange
} from '../types/bureau-activity.types';
import type { TeamMemberWithPlanning } from '../../equipe/types/team.types';

/**
 * Hook for fetching bureau users list
 */
export const useBureauUsers = () => {
  const [users, setUsers] = useState<BureauUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await bureauActivityAPI.getBureauUsers();
      if (response.success) {
        setUsers(response.data);
      }
    } catch (err) {
      setError('Erreur lors du chargement des utilisateurs');
      console.error('Error fetching bureau users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return { users, loading, error, refetch: fetchUsers };
};

/**
 * Hook for fetching daily activity data
 */
export const useBureauActivity = (dateRange: DateRange, userId?: string) => {
  const [activities, setActivities] = useState<DailyActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchActivity = useCallback(async () => {
    try {
      setLoading(true);
      const response = await bureauActivityAPI.getBureauActivity(dateRange, userId);
      if (response.success) {
        setActivities(response.data.activities);
      }
    } catch (err) {
      setError('Erreur lors du chargement des activités');
      console.error('Error fetching bureau activity:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange.startDate, dateRange.endDate, userId]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  return { activities, loading, error, refetch: fetchActivity };
};

/**
 * Hook for fetching activity summary
 */
export const useBureauSummary = (dateRange: DateRange) => {
  const [summary, setSummary] = useState<UserActivitySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      const response = await bureauActivityAPI.getBureauSummary(dateRange);
      if (response.success) {
        setSummary(response.data.summary);
      }
    } catch (err) {
      setError('Erreur lors du chargement du résumé');
      console.error('Error fetching bureau summary:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return { summary, loading, error, refetch: fetchSummary };
};

/**
 * Hook for fetching overall stats
 */
export const useBureauStats = (dateRange: DateRange) => {
  const [stats, setStats] = useState<BureauStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await bureauActivityAPI.getBureauStats(dateRange);
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      setError('Erreur lors du chargement des statistiques');
      console.error('Error fetching bureau stats:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, loading, error, refetch: fetchStats };
};

/**
 * Hook for fetching real-time activity
 */
export const useRealtimeActivity = (userId: string | null) => {
  const [data, setData] = useState<RealtimeActivity | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRealtime = useCallback(async () => {
    if (!userId) {
      setData(null);
      return;
    }

    try {
      setLoading(true);
      const response = await bureauActivityAPI.getRealtimeActivity(userId);
      if (response.success) {
        setData(response.data);
      }
    } catch (err) {
      setError('Erreur lors du chargement de l\'activité en temps réel');
      console.error('Error fetching realtime activity:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchRealtime();
    // Refresh every 30 seconds for real-time view
    const interval = setInterval(fetchRealtime, 30000);
    return () => clearInterval(interval);
  }, [fetchRealtime]);

  return { data, loading, error, refetch: fetchRealtime };
};

/**
 * Hook for fetching user activity details
 */
export const useUserActivityDetails = (userId: string | null, dateRange: DateRange) => {
  const [details, setDetails] = useState<UserActivityDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDetails = useCallback(async () => {
    if (!userId) {
      setDetails(null);
      return;
    }

    try {
      setLoading(true);
      const response = await bureauActivityAPI.getUserActivityDetails(userId, dateRange);
      if (response.success) {
        setDetails(response.data);
      }
    } catch (err) {
      setError('Erreur lors du chargement des détails');
      console.error('Error fetching user activity details:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  return { details, loading, error, refetch: fetchDetails };
};

/**
 * Hook for date range management with presets
 */
export const useDateRange = () => {
  const getDefaultRange = (): DateRange => {
    const today = new Date();
    return {
      startDate: format(today, 'yyyy-MM-dd'),
      endDate: format(today, 'yyyy-MM-dd')
    };
  };

  const [dateRange, setDateRange] = useState<DateRange>(getDefaultRange);

  const setPreset = useCallback((preset: 'today' | 'week' | 'month' | 'quarter') => {
    const end = new Date();
    const start = new Date();

    switch (preset) {
      case 'today':
        break;
      case 'week':
        start.setDate(start.getDate() - 7);
        break;
      case 'month':
        start.setMonth(start.getMonth() - 1);
        break;
      case 'quarter':
        start.setMonth(start.getMonth() - 3);
        break;
    }

    setDateRange({
      startDate: format(start, 'yyyy-MM-dd'),
      endDate: format(end, 'yyyy-MM-dd')
    });
  }, []);

  return { dateRange, setDateRange, setPreset };
};

/**
 * Hook for fetching user planning/schedule
 */
export const useUserPlanning = (userId: string | null) => {
  const [allPlanning, setAllPlanning] = useState<TeamMemberWithPlanning[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get current week's Monday
  const weekStart = useMemo(() => {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(now.setDate(diff));
    return format(monday, 'yyyy-MM-dd');
  }, []);

  const fetchPlanning = useCallback(async () => {
    if (!userId) {
      setAllPlanning([]);
      return;
    }

    try {
      setLoading(true);
      const data = await teamAPI.getTeamWeekPlanning(weekStart);
      setAllPlanning(data);
    } catch (err) {
      setError('Erreur lors du chargement du planning');
      console.error('Error fetching user planning:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, weekStart]);

  useEffect(() => {
    fetchPlanning();
  }, [fetchPlanning]);

  // Filter to get just the selected user's planning
  const userPlanning = useMemo(() => {
    if (!userId || allPlanning.length === 0) return null;
    return allPlanning.find(member => member.user.id === userId) || null;
  }, [userId, allPlanning]);

  return { planning: userPlanning, loading, error, refetch: fetchPlanning };
};

/**
 * Hook for fetching ALL team planning (for timeline availability display)
 */
export const useAllTeamPlanning = (dateRange: DateRange) => {
  const [planningByWeek, setPlanningByWeek] = useState<Map<string, TeamMemberWithPlanning[]>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get all week starts within the date range
  const weekStarts = useMemo(() => {
    const weeks: string[] = [];
    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);

    // Get Monday of start week
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(start);
    monday.setDate(diff);

    while (monday <= end) {
      weeks.push(format(monday, 'yyyy-MM-dd'));
      monday.setDate(monday.getDate() + 7);
    }

    return weeks;
  }, [dateRange.startDate, dateRange.endDate]);

  const fetchAllPlanning = useCallback(async () => {
    if (weekStarts.length === 0) return;

    try {
      setLoading(true);
      const results = new Map<string, TeamMemberWithPlanning[]>();

      // Fetch planning for each week in parallel
      const promises = weekStarts.map(async (weekStart) => {
        try {
          const data = await teamAPI.getTeamWeekPlanning(weekStart);
          return { weekStart, data };
        } catch {
          return { weekStart, data: [] };
        }
      });

      const responses = await Promise.all(promises);
      responses.forEach(({ weekStart, data }) => {
        results.set(weekStart, data);
      });

      setPlanningByWeek(results);
    } catch (err) {
      setError('Erreur lors du chargement des plannings');
      console.error('Error fetching team planning:', err);
    } finally {
      setLoading(false);
    }
  }, [weekStarts]);

  useEffect(() => {
    fetchAllPlanning();
  }, [fetchAllPlanning]);

  // Helper to get planning for a specific user on a specific date
  const getPlanningForUserDate = useCallback((userId: string, date: string): {
    dayPlanning: { start: string; end: string; enabled: boolean; isRemote: boolean } | null;
    appointments: { start: string; end: string; title: string }[];
  } => {
    // Parse the date and get local date components to avoid UTC/local timezone issues
    // This handles ISO strings like "2025-12-09T23:00:00.000Z" which represent Dec 10 in CET
    const parsedDate = new Date(date);
    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');
    const normalizedDate = `${year}-${month}-${day}`;
    const dateObj = parsedDate;

    const dayOfWeek = dateObj.getDay();
    const diff = dateObj.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const monday = new Date(dateObj);
    monday.setDate(diff);
    const weekStart = format(monday, 'yyyy-MM-dd');

    const weekPlanning = planningByWeek.get(weekStart);
    if (!weekPlanning) return { dayPlanning: null, appointments: [] };

    const userPlanning = weekPlanning.find(p => p.user.id === userId);
    if (!userPlanning) return { dayPlanning: null, appointments: [] };

    const dayMap: Record<number, keyof TeamMemberWithPlanning['planning']> = {
      0: 'sunday',
      1: 'monday',
      2: 'tuesday',
      3: 'wednesday',
      4: 'thursday',
      5: 'friday',
      6: 'saturday'
    };

    const dayKey = dayMap[dateObj.getDay()];
    const planning = userPlanning.planning[dayKey];

    // Filter appointments for this date (compare normalized dates)
    const appointments = userPlanning.appointments
      .filter(apt => apt.date.split('T')[0] === normalizedDate)
      .map(apt => ({
        start: apt.startTime,
        end: apt.endTime,
        title: apt.title
      }));

    return {
      dayPlanning: planning.enabled ? {
        start: planning.start,
        end: planning.end,
        enabled: planning.enabled,
        isRemote: planning.workLocation === 'remote'
      } : null,
      appointments
    };
  }, [planningByWeek]);

  return {
    planningByWeek,
    getPlanningForUserDate,
    loading,
    error,
    refetch: fetchAllPlanning
  };
};
